import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Search, ArrowLeft, MessageSquareText } from "lucide-react";
import { api } from "../lib/api";
import VehicleCard from "../components/VehicleCard";
import GlobalSearchBar from "../components/GlobalSearchBar";
import Seo from "../components/Seo";
import { siteConfig } from "../config";

/**
 * Global inventory search results (v6.3).
 *
 * Renders /inventory?search=<query>. The backend handles cross-status search,
 * smart parsing of "new"/"used"/"ev"/"diesel"/year tokens, and exact
 * VIN/stock short-circuits (handled in GlobalSearchBar before we get here).
 *
 * Customer can refine results with three tabs: All / New only / Used only.
 * Tabs are LOCAL refinements — they do not refire the backend with a
 * status filter, so we never accidentally hide a vehicle the user searched
 * for that lives in the other section.
 */
export default function InventorySearchResults() {
  const loc = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const query = params.get("search") || params.get("q") || "";

  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!query.trim()) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/inventory/search", { params: { q: query, limit: 60 } })
      .then(({ data }) => {
        if (cancelled) return;
        // exact match short-circuit — redirect to VDP
        if (data?.exact_match?.slug) {
          const path =
            data.exact_match.status === "used"
              ? `/used-vehicles/${data.exact_match.slug}`
              : `/new-vehicles/${data.exact_match.slug}`;
          navigate(path, { replace: true });
          return;
        }
        setData(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, navigate]);

  const results = data?.results || [];
  const filtered = useMemo(() => {
    if (tab === "new") return results.filter((v) => v.status === "new");
    if (tab === "used") return results.filter((v) => v.status === "used");
    return results;
  }, [results, tab]);

  return (
    <div className="ssc-container ssc-section" data-testid="inventory-search-results">
      <Seo
        title={query ? `Search: ${query} | Scott Sells Chevys` : "Search Inventory | Scott Sells Chevys"}
        description={`Search ${query ? `for "${query}" across` : ""} all new and used Chevrolet inventory at Byers Chevrolet with Scott Hall.`}
      />

      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="ssc-btn ssc-btn-outline-dark"
          data-testid="inventory-search-back"
          aria-label="Go back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="ssc-h1 mb-0 flex-1" data-testid="inventory-search-h1">
          {query ? `Search: ${query}` : "Search Inventory"}
        </h1>
      </div>

      <div className="mb-4" data-testid="inventory-search-bar-wrap">
        <GlobalSearchBar variant="page" placeholder="Search by model, stock #, VIN, new or used…" />
      </div>

      {loading && (
        <div className="text-sm text-[var(--ssc-ink-2)]" data-testid="inventory-search-loading">
          Searching all inventory…
        </div>
      )}
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="inventory-search-error">
          {error}
        </div>
      )}

      {!loading && data && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4" role="tablist" aria-label="Filter by condition">
            {[
              ["all", `All (${data.total || 0})`],
              ["new", `New (${data.new_count || 0})`],
              ["used", `Used (${data.used_count || 0})`],
            ].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full border-2 transition ${
                  tab === id
                    ? "bg-[var(--ssc-navy)] text-white border-[var(--ssc-navy)]"
                    : "bg-white text-[var(--ssc-navy)] border-[var(--ssc-line)] hover:border-[var(--ssc-navy)]"
                }`}
                data-testid={`inventory-search-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <NoResults query={query} />
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid="inventory-search-grid"
            >
              {filtered.map((v) => (
                <VehicleCard key={v.vin || v.stock_number} vehicle={v} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NoResults({ query }) {
  return (
    <div
      className="rounded-2xl border border-[var(--ssc-line)] bg-[var(--ssc-paper)] p-8 text-center"
      data-testid="inventory-search-empty"
    >
      <Search className="mx-auto mb-3 text-[var(--ssc-ink-2)]" size={40} />
      <div className="text-lg font-extrabold text-[var(--ssc-navy)] mb-1">
        No exact match found{query ? ` for "${query}"` : ""}
      </div>
      <p className="text-sm text-[var(--ssc-ink-2)] max-w-md mx-auto mb-5">
        Try a shorter query, a model name (Trax, Equinox, Silverado), or text
        Scott directly — he can probably find it or get one in.
      </p>
      <div className="flex flex-wrap gap-2 justify-center" data-testid="inventory-search-empty-ctas">
        <Link to="/inventory" className="ssc-btn ssc-btn-primary" data-testid="empty-search-all">
          Search All Inventory
        </Link>
        <Link to="/new-vehicles" className="ssc-btn ssc-btn-outline-blue" data-testid="empty-browse-new">
          Browse New
        </Link>
        <Link to="/used-vehicles" className="ssc-btn ssc-btn-outline-red" data-testid="empty-browse-used">
          Browse Used
        </Link>
        <a
          href={siteConfig.scott.phoneSms}
          className="ssc-btn ssc-btn-navy"
          data-testid="empty-text-scott"
        >
          <MessageSquareText size={16} /> Text Scott
        </a>
      </div>
    </div>
  );
}
